import {useNavigate, useSearchParams} from 'react-router-dom';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useResetPassword, type ResetPasswordBody} from 'api/generated';
import {toast} from 'react-toastify';
import {usePersistentStore} from 'stores';
import {Button, InputSecret, Transitioner} from 'components';

const schema = z.object({
  token: z.string(),
  password: z.string().min(6, 'Password must be at least 6 character(s)'),
});

interface IFormResetPasswordProps {}

const FormResetPassword = ({}: IFormResetPasswordProps) => {
  const navigate = useNavigate();
  const setAuthToken = usePersistentStore(s => s.setAuthToken);

  const resetPassword = useResetPassword();
  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordBody) => {
    const {data: res} = await resetPassword.mutateAsync({data});
    if (res.data) {
      setAuthToken(res.data.jwt);
      navigate('/', {replace: true});
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  return (
    <section className="flex flex-col flex-1 p-6 lg:px-8">
      <Transitioner>
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h1 className="text-center text-2xl/9 font-bold tracking-tight dark:text-white">Reset your password</h1>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
            <input type="hidden" readOnly value={token} {...register('token')} />
            <InputSecret
              label="New Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={errors.password?.message}
            />
            <div className="mt-5">
              <Button type="submit" text="Submit" fullWidth size="small" loading={resetPassword.isPending} />
            </div>
          </form>
        </div>
      </Transitioner>
    </section>
  );
};

export default FormResetPassword;
